<template>
  <va-form
    :id="isEdit ? page.id : ''"
    ref="form"
    v-model="form"
    :item="page"
    :disable-redirect="isEdit"
    :before-save="beforeSave"
    :after-save="handleAfterSave"
    :modify-before-send="prepareFormBeforeSave"
    :custom-redirect-route="customRedirectRoute"
    :success-redirect-link="successRedirectLink"
    :required-fields-set="requiredFieldsSet"
    @finishedSaving="finishedSaving">
    <v-row>
      <!--  Delete alternative content Modal --->
      <Modal
        width="400"
        :value="displayDeleteAlternativeContentModal"
        :title="$t('resources.pages.page_alternative_content_delete_modal_title')"
        :subtitle="$t('resources.pages.page_alternative_content_delete_modal_subtitle')"
        :cta-text="$t('generic.buttons.delete')"
        :cta-color="'error'"
        :dialog-icon="'danger'"
        @confirm="confirmAlternativeContentDelete"
        @cancel="cancelAlternativeContentDelete">
      </Modal>
      <!--  Delete alternative content Modal END--->
      <!-- Delete dynamic content modal -->
      <Modal
        width="400"
        :value="displayDeleteDynamicContentModal"
        :title="$t('resources.pages.page_dynamic_content_delete_modal_title')"
        :subtitle="$t('resources.pages.page_dynamic_content_delete_modal_subtitle')"
        :cta-text="$t('generic.buttons.delete')"
        :cta-color="'error'"
        :dialog-icon="'danger'"
        @confirm="confirmDynamicContentDelete"
        @cancel="cancelDynamicContentDelete">
      </Modal>
      <!-- Delete label START -->
      <Modal
        width="400"
        :value="displayDeleteLabelModal"
        :title="$t('resources.labels.label_delete_modal_title')"
        :subtitle="$t('resources.labels.label_delete_modal_subtitle')"
        :cta-text="$t('generic.buttons.delete')"
        :cta-color="'error'"
        :dialog-icon="'danger'"
        @confirm="confirmDeleteLabel"
        @cancel="cancelDeleteLabel">
      </Modal>
      <!-- Delete label END -->
      <!-- Delete Ringba tag START -->
      <Modal
        width="400"
        :value="displayDeleteRingbaTagModal"
        :title="$t('resources.ringba_tags.ringba_tag_delete_modal_title')"
        :cta-text="$t('generic.buttons.delete')"
        :cta-color="'error'"
        :dialog-icon="'danger'"
        @confirm="confirmDeleteRingbaTag"
        @cancel="cancelDeleteRingbaTag">
      </Modal>
      <!-- Delete Ringba tag END -->
      <!--- Page version change Modal -->
      <Modal
        width="448"
        :value="displayVersionHistoryModal"
        :title="$t('resources.pages.page_history_modal_title')"
        :cta-text="$t('resources.pages.page_history_modal_cta_text')"
        :cta-disabled="!selectedVersion"
        :cta-loading="changePageVersionLoading"
        @confirm="confirmVersionChange"
        @cancel="cancelVersionChange"
        actions-border>
        <v-select
          class="spacing-top--20"
          v-model="selectedVersion"
          :items="versionsOptions"
          :label="$t('resources.pages.page_version_select_label')"
          :placeholder="$t('resources.pages.page_version_select_placeholder')"
          persistent-placeholder
          :menu-props="{ offsetY: true, offsetOverflow: true }"
          @change="handleVersionChange"
          hide-details="auto">
          <template v-slot:append>
            <v-icon>mdi-chevron-down</v-icon>
          </template>
          <template v-slot:item="{ item }">
            <div class="v-list-item__content">
              <div class="v-list-item__title">{{ item.text }}</div>
            </div>
            <div v-if="item.disabled" class="ml-auto">
              <v-chip class="in-use-chip" :color="'subtitle'" outlined :text-color="'subtitle'">
                {{ $t("resources.pages.version_in_use") }}
              </v-chip>
            </div>
          </template>
        </v-select>
        <span v-if="!selectedVersion" class="preview_selected_version">{{
          $t("resources.pages.preview_selected_version_disclaimer")
        }}</span>
        <div v-else class="preview-version-label" @click="previewPageVersion">
          <v-icon size="16">mdi-dock-window</v-icon> {{ $t("resources.pages.preview_this_version") }}
        </div>
      </Modal>
      <!--- Page version change Modal END -->
      <v-col cols="8">
        <!-- Content -->
        <v-card class="spacing-bottom--24">
          <v-card-title>{{ $t("resources.pages.page_content_block_title") }} </v-card-title>
          <div class="pre-header-input spacing-bottom--16" v-if="ability.can('view', 'pre-headline field')">
            <span class="label">{{ $t("resources.pages.pre_header") }}</span>
            <div class="pre-header-wrapper">
              <div class="input-wrapper">
                <span> {{ $t("resources.pages.pre_header_subtitle") }} </span>
                <v-switch
                  inset
                  color="primary"
                  :hide-details="'auto'"
                  :ripple="false"
                  v-model="preHeaderVisible"
                  @change="animatePreHeader"></v-switch>
              </div>
              <va-tinymce-input
                ref="preHeaderTextField"
                class="pre-header-input"
                :class="preHeaderVisible ? 'visible' : 'hidden'"
                source="preHeader"
                :current-content="form.preHeader"
                :editor-height="350"
                :stripped-down="true"
                :imageUploadQueue.sync="preHeaderImageUploadQueue"
                @update="updatePreHeader" />
            </div>
          </div>

          <div class="d-flex align-center spacing-bottom--16">
            <va-text-input
              class="spacing-right--16"
              source="headline"
              v-model="form.headline"
              required
              :label="$t('resources.pages.headline')"
              :placeholder="$t('resources.pages.headline_placeholder')"></va-text-input>
            <v-checkbox
              source="showHeadline"
              v-model="form.showHeadline"
              :label="$t('resources.pages.page_headline_hide')"
              inset
              color="primary"
              class="spacing-top--19"
              :hide-details="'auto'"
              :ripple="false"
              :true-value="false"
              :false-value="true"></v-checkbox>
          </div>

          <va-tinymce-input
            source="mainContent"
            :title="$t('resources.pages.page_main_content')"
            :current-content="form.mainContent"
            :editor-height="739"
            :key="versionKey"
            :display-errors="submitAttempted"
            :imageUploadQueue.sync="imageUploadQueue"
            required
            @update="updateMainContent" />

          <div v-if="ability.can('view', 'server-side cloaking')" class="server-side-cloaking-wrapper">
            <div>
              <span>{{ $t("resources.pages.enable_server_side_cloaking") }}</span>
              <v-switch
                v-model="form.serverSideContentSplit"
                inset
                color="primary"
                :hide-details="'auto'"
                :ripple="false"></v-switch>
            </div>
            <div class="error-wrapper" v-for="error in formErrors.serverSideContentSplit" :key="error">
              {{ error }}
            </div>
          </div>

          <!--- Alternative content block -->
          <div class="alternative-content" v-if="ability.can('view', 'alternative content')">
            <div class="d-flex items-center spacing-top--16 align-center">
              <div>
                <div class="alternative-content__title">
                  {{ $t("resources.pages.page_alternative_content_block_title") }}
                </div>
                <div class="alternative-content__subtitle">
                  {{ $t("resources.pages.page_alternative_content_block_subtitle") }}
                </div>
              </div>
              <template v-if="!alternativeContentVisible">
                <v-btn
                  @click="alternativeContentVisible = !alternativeContentVisible"
                  class="alternative-content__add-btn left-icon ml-auto"
                  color="secondary-btn"
                  plain>
                  <plus-icon class="fill--primary mr-2"></plus-icon>
                  {{ $t("resources.pages.page_alternative_content_block_add") }}
                </v-btn>
              </template>
              <template v-else>
                <v-btn
                  @click="displayDeleteAlternativeContentModal = true"
                  class="alternative-content__delete-btn left-icon ml-auto"
                  color="error"
                  plain>
                  <v-icon small>mdi-trash-can-outline</v-icon>
                  {{ $t("resources.pages.page_alternative_content_block_delete") }}
                </v-btn>
              </template>
            </div>
            <v-expand-transition>
              <div v-if="alternativeContentVisible" class="alternative-content__inner">
                <div>
                  <va-autocomplete-input
                    ref="alternativeContentAutocomplete"
                    required
                    class="create-alternative-content-select"
                    clearable
                    :value="form.sharedAlternativeContent"
                    source="sharedAlternativeContent"
                    reference="alternative_contents"
                    :label="$t('resources.pages.alternative_content_creation_method')"
                    :placeholder="$t('resources.pages.alternative_content_creation_method_placeholder')"
                    item-value="@id"
                    :alternativeDropdownView="true"
                    :min-chars="0"
                    :items-per-page="5"
                    :append-item="pageAlternativeContent"
                    showLoadMore
                    customItem
                    @change="onCreateAlternativeContentChange">
                    <template v-slot:item="{ item, on }">
                      <v-list-item v-on="on" v-show="!item.hidden">
                        <v-list-item-content>
                          <v-list-item-title class="alternative-content-item">
                            <div>{{ item.name }}</div>
                            <router-link :to="`/alternative-contents/${item.id}/edit`" target="_blank">
                              <v-icon color="primary" size="16">mdi-open-in-new</v-icon>
                              <span>View</span>
                            </router-link>
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                    <template #append-item-outer>
                      <empty-select-search-list
                        v-if="alternativeContentEmpty()"
                        resource-name="alternative_contents"
                        :empty-title="$t('generic.no_results_found')"
                        @click="createResourceHandler">
                      </empty-select-search-list>
                      <v-list-item
                        class="create-alternative-content-item"
                        :input-value="form.sharedAlternativeContent === pageAlternativeContent['@id']"
                        @click="onCreateAlternativeContentChange(pageAlternativeContent['@id'])">
                        <v-list-item-action class="create-alternative-content-action">
                          <v-icon size="16" color="primary">mdi-plus</v-icon>
                          <span>
                            {{ $t("resources.pages.alternative_content_create") }}
                          </span>
                        </v-list-item-action>
                      </v-list-item>
                    </template>
                    <template #no-data>
                      <empty-select-search-list
                        resource-name="alternative_contents"
                        :empty-title="$t('generic.no_results_found')"
                        @click="createResourceHandler">
                      </empty-select-search-list>
                    </template>
                  </va-autocomplete-input>
                </div>
                <v-expand-transition>
                  <div
                    v-if="
                      form.sharedAlternativeContent && form.sharedAlternativeContent !== pageAlternativeContent['@id']
                    ">
                    <v-text-field
                      v-model="form.sharedAlternativeContentUrl"
                      required
                      persistent-placeholder
                      hide-details="auto"
                      :disabled="sharedContentHasUrl()"
                      :rules="[rules.url]"
                      :error="!!formErrors.sharedAlternativeContentUrl"
                      :error-messages="formErrors.sharedAlternativeContentUrl"
                      :label="$t('resources.pages.alternative_content_url')"
                      :placeholder="$t('resources.pages.alternative_content_url_placeholder')"></v-text-field>
                    <div class="alternative-no-url-disclaimer" v-if="sharedContentHasUrl()">
                      <v-icon size="24" color="info"> mdi-information </v-icon>
                      <span>
                        <span>{{ $t("resources.pages.alternative_content_no_url_disclaimer") }}</span>
                        <span @click="openGlobalAlternativeContent" class="alternative-no-url-disclaimer-link">{{
                          $t("resources.pages.alternative_content_disclaimer_url")
                        }}</span>
                        <span>.</span>
                      </span>
                    </div>
                  </div>
                </v-expand-transition>
                <v-expand-transition>
                  <div v-if="createPageAlternativeContent">
                    <div>
                      <div class="d-flex spacing-bottom--16">
                        <va-autocomplete-input
                          class="mr-2 prepend-icon-hidden"
                          :label="$t('resources.pages.page_alternative_content_countries_label')"
                          v-model="alternativeContentConditions.selectedCountries"
                          :choices="countriesData"
                          :source="'loneAlternativeContent.conditionals.selectedCountries'"
                          cache-items
                          :placeholder="$t('resources.pages.page_alternative_content_countries_placeholder')"
                          multiple></va-autocomplete-input>

                        <va-autocomplete-input
                          class="ml-2 prepend-icon-hidden"
                          :label="$t('resources.pages.page_alternative_content_states_label')"
                          v-model="alternativeContentConditions.selectedStates"
                          :choices="statesData"
                          :source="'loneAlternativeContent.conditionals.selectedStates'"
                          cache-items
                          :placeholder="$t('resources.pages.page_alternative_content_states_placeholder')"
                          multiple></va-autocomplete-input>
                      </div>
                      <div class="d-flex spacing-bottom--16">
                        <va-autocomplete-input
                          class="mr-2 prepend-icon-hidden"
                          :label="$t('resources.pages.page_alternative_content_cities_label')"
                          v-model="alternativeContentConditions.selectedCities"
                          :choices="citiesData"
                          :source="'loneAlternativeContent.conditionals.selectedCities'"
                          cache-items
                          :placeholder="$t('resources.pages.page_alternative_content_cities_placeholder')"
                          multiple
                          hideSelectAll></va-autocomplete-input>

                        <va-autocomplete-input
                          class="ml-2 prepend-icon-hidden"
                          :label="$t('resources.pages.page_alternative_content_counties_label')"
                          v-model="alternativeContentConditions.selectedCounties"
                          :choices="countiesData"
                          :source="'loneAlternativeContent.conditionals.selectedCounties'"
                          cache-items
                          :placeholder="$t('resources.pages.page_alternative_content_counties_placeholder')"
                          multiple></va-autocomplete-input>
                      </div>
                      <div class="d-flex spacing-bottom--16">
                        <va-select-input
                          :label="$t('resources.pages.page_alternative_content_devices_label')"
                          class="device__select prepend-icon-hidden"
                          multiple
                          v-model="alternativeContentConditions.selectedDevices"
                          :choices="devices"
                          :source="'loneAlternativeContent.conditionals.selectedDevices'"
                          cache-items
                          :placeholder="$t('resources.pages.page_alternative_content_devices_placeholder')"
                          :menu-props="{ offsetY: true, offsetOverflow: true }"></va-select-input>
                      </div>
                    </div>
                    <v-text-field
                      class="spacing-bottom--16"
                      v-model="form.loneAlternativeContent.headline"
                      required
                      persistent-placeholder
                      hide-details
                      :label="$t('resources.pages.page_alternative_content_headline')"
                      :placeholder="$t('resources.pages.headline_placeholder')"></v-text-field>

                    <div class="d-flex py-3">
                      <v-switch
                        class="enable-utm-parameters-switch"
                        :label="$t('resources.pages.page_alternative_content_utm_parameters_label')"
                        v-model="alternativeContentConditions.enableUTMParameters"
                        @change="enableUTMParametersChange"
                        color="primary"
                        :ripple="false"
                        hide-details
                        inset></v-switch>
                      <info-tooltip
                        class="enable-utm-parameters-switch-tooltip"
                        :info="$t('resources.pages.page_alternative_content_utm_parameters_tooltip')"></info-tooltip>
                    </div>

                    <!-- UTM params -->
                    <v-expand-transition>
                      <div v-if="alternativeContentConditions.enableUTMParameters" class="expandable-content">
                        <div>
                          <div class="page-alternative-content-click-parameters-title spacing-top--16">
                            {{ $t("resources.pages.page_alternative_content_click_parameters") }}
                          </div>
                          <div>
                            <v-checkbox
                              v-model="alternativeContentConditions.checkUniqueClickId"
                              :label="$t('resources.pages.page_alternative_content_check_unique')" />
                          </div>
                        </div>
                        <div class="d-flex spacing-bottom--16">
                          <va-text-input
                            class="domain-details__card--url flex-1 mr-2"
                            v-model.trim="alternativeContentConditions.utmSource"
                            :prefix="'UTM_SOURCE='"
                            :label="'Source'"
                            :placeholder="'SOURCE'"></va-text-input>
                          <va-text-input
                            class="domain-details__card--url flex-1 ml-2"
                            v-model.trim="alternativeContentConditions.utmMedium"
                            :prefix="'UTM_MEDIUM='"
                            :label="'Medium'"
                            :placeholder="'MEDIUM'"></va-text-input>
                        </div>

                        <div class="d-flex spacing-bottom--16">
                          <va-text-input
                            class="domain-details__card--url flex-1 mr-2"
                            v-model.trim="alternativeContentConditions.utmCampaign"
                            :prefix="'UTM_CAMPAIGN='"
                            :label="'Campaign'"
                            :placeholder="'CAMPAIGN'"></va-text-input>
                          <va-text-input
                            class="domain-details__card--url flex-1 ml-2"
                            v-model.trim="alternativeContentConditions.utmTerm"
                            :prefix="'UTM_TERM='"
                            :label="'Term'"
                            :placeholder="'TERM'"></va-text-input>
                        </div>

                        <div class="d-flex">
                          <va-text-input
                            class="domain-details__card--url pr-2 max-w-50"
                            v-model.trim="alternativeContentConditions.utmContent"
                            :prefix="'UTM_CONTENT='"
                            :label="'Content'"
                            :placeholder="'CONTENT'"></va-text-input>
                        </div>
                      </div>
                    </v-expand-transition>

                    <va-tinymce-input
                      class="spacing-bottom--16 spacing-top--16"
                      :title="$t('resources.pages.page_alternative_content')"
                      :current-content="alternativeContent"
                      :editor-height="739"
                      :imageUploadQueue.sync="alternativeContentImageUploadQueue"
                      :key="versionKey"
                      @update="updateAlternativeContent" />
                    <VaJsonInput
                      :label="$t('resources.pages.page_alternative_content_json_label')"
                      v-model="alternativeContentConditionsJson"></VaJsonInput>
                  </div>
                </v-expand-transition>
              </div>
            </v-expand-transition>
            <!--- Alternative content block END-->
          </div>

          <!-- Dynamic content block -->
          <div class="dynamic-content" v-if="ability.can('view', 'calendars')">
            <div class="d-flex items-center spacing-top--16 align-center">
              <div>
                <div class="dynamic-content__title">
                  {{ $t("resources.pages.page_dynamic_content_block_title") }}
                </div>
                <div class="dynamic-content__subtitle">
                  {{ $t("resources.pages.page_dynamic_content_block_subtitle") }}
                </div>
              </div>
              <template v-if="!dynamicContentVisible">
                <v-btn
                  @click="dynamicContentVisible = !dynamicContentVisible"
                  class="dynamic-content__add-btn left-icon ml-auto"
                  color="secondary-btn"
                  plain>
                  <plus-icon class="fill--primary mr-2"></plus-icon>
                  {{ $t("resources.pages.page_dynamic_content_block_add") }}
                </v-btn>
              </template>
              <template v-else>
                <v-btn
                  @click="displayDeleteDynamicContentModal = true"
                  class="dynamic-content__delete-btn left-icon ml-auto"
                  color="error"
                  plain>
                  <v-icon small>mdi-trash-can-outline</v-icon>
                  {{ $t("resources.pages.page_dynamic_content_block_delete") }}
                </v-btn>
              </template>
            </div>
            <v-expand-transition>
              <div v-if="dynamicContentVisible">
                <va-autocomplete-input
                  class="spacing-top--16"
                  v-model="form.calendar"
                  ref="calendarsAutocomplete"
                  :items-per-page="5"
                  :min-chars="0"
                  :label="$t('resources.pages.page_calendar_label')"
                  :placeholder="$t('resources.pages.page_calendar_placeholder')"
                  source="calendar"
                  reference="calendars"
                  item-text="name"
                  item-value="@id"
                  :alternativeDropdownView="true"
                  showLoadMore>
                  <template v-slot:[`no-data`]>
                    <empty-select-search-list
                      resource-name="calendars"
                      :empty-title="$t('resources.calendars.no_calendars_available')"
                      @click="createResourceHandler">
                    </empty-select-search-list>
                  </template>
                </va-autocomplete-input>
                <v-expand-transition>
                  <div v-if="form.calendar">
                    <v-text-field
                      class="spacing-top--16"
                      hide-details
                      persistent-placeholder
                      v-model="form.calendarContent.headline"
                      :label="$t('resources.pages.page_calendar_headline_label')"
                      :placeholder="$t('resources.pages.page_calendar_headline_placeholder')"></v-text-field>
                    <va-tinymce-input
                      class="spacing-top--16"
                      :title="$t('resources.pages.page_calendar_content_label')"
                      :current-content="dynamicContent"
                      :editor-height="739"
                      :imageUploadQueue.sync="dynamicContentImageUploadQueue"
                      :key="versionKey"
                      @update="updateDynamicContent" />
                  </div>
                </v-expand-transition>
              </div>
            </v-expand-transition>
          </div>
        </v-card>

        <!-- Images -->
        <va-expandable-wrapper
          class="spacing-bottom--24"
          :title="$t('resources.pages.page_images_block_title')"
          :open="imagesBlockVisible">
          <VaImageDragAndDropInput
            :item-id="form.featuredImage"
            :label="$t('resources.pages.page_featured_image')"
            @update:photo="updateFeaturedImage"></VaImageDragAndDropInput>
          <p class="page-subcard-disclaimer spacing-bottom--16">
            {{ $t("resources.pages.page_featured_image_disclaimer") }}
          </p>
          <va-text-input
            class="spacing-bottom--16"
            v-model="form.featuredImageCaption"
            :label="$t('resources.pages.featured_caption')"
            :placeholder="$t('resources.pages.featured_caption_placeholder')"
            source="featuredImageCaption"></va-text-input>

          <VaImageDragAndDropInput
            v-if="form.type !== 1"
            :item-id="form.thumbnailImage"
            :label="$t('resources.pages.page_thumbnail_image')"
            @update:photo="updateThumbnailImage"></VaImageDragAndDropInput>
          <p v-if="form.type !== 1" class="page-subcard-disclaimer spacing-bottom--16">
            {{ $t("resources.pages.page_thumbnail_image_disclaimer") }}
          </p>
        </va-expandable-wrapper>

        <!-- Excerpt -->
        <va-expandable-wrapper
          v-if="form.type !== 1"
          class="spacing-bottom--24"
          :title="$t('resources.pages.page_excerpt_block_title')"
          :open="false">
          <va-tinymce-input
            source="excerpt"
            :title="$t('resources.pages.excerpt_text')"
            :current-content="form.excerpt"
            :editor-height="275"
            :info="$t('resources.pages.excerpt_tooltip_text')"
            :stripped-down="true"
            :imageUploadQueue.sync="excerptImageUploadQueue"
            @update="updateExcerptText" />
        </va-expandable-wrapper>

        <!-- Code -->
        <va-expandable-wrapper :title="$t('resources.pages.page_code_block_title')" :open="codeBlockVisible">
          <code-input
            v-model="form.headCustomCode"
            class="spacing-bottom--16"
            :info="$t('resources.pages.page_head_section_code_tooltip_text')"
            :title="$t('resources.pages.page_head_section_code')"></code-input>

          <code-input
            v-model="form.bodyCustomCode"
            :info="$t('resources.pages.page_body_section_code_tooltip_text')"
            :title="$t('resources.pages.page_body_section_code')"></code-input>
        </va-expandable-wrapper>
      </v-col>

      <v-col cols="4">
        <!-- Details -->
        <v-card class="spacing-bottom--24">
          <v-card-title class="card-title details-title">
            <div>
              <v-icon class="card-icon">mdi-web</v-icon>
              {{ $t("resources.pages.page_details_block_title") }}
            </div>
            <v-tooltip top v-if="statusObject.tooltipText">
              <template v-slot:activator="{ on, attrs }">
                <v-chip :color="statusObject.chipColor" class="status-chip" v-bind="attrs" v-on="on">
                  {{ statusObject.text }}
                </v-chip>
              </template>
              {{ statusObject.tooltipText }}
            </v-tooltip>
            <v-chip v-else :color="statusObject.chipColor" class="status-chip chip-outlined">
              {{ statusObject.text }}
            </v-chip>
          </v-card-title>

          <va-autocomplete-input
            :disabled="isDomainProvided || isEdit"
            class="spacing-bottom--16"
            required
            source="domain"
            v-model="form.domain"
            :label="$t('resources.pages.page_domain')"
            :placeholder="$t('resources.pages.page_domain_placeholder')"
            :choices="domainsList"
            :min-chars="1"
            cache-items
            @change="domainChanged"></va-autocomplete-input>

          <va-slug-checker
            v-model="form.slug"
            :domain="form.domain"
            :label="$t('resources.pages.page_slug')"
            :no-check="isEdit"
            :source="'slug'"
            :edit-form="isEdit"
            @updateLoading="val => (this.isLoading = val)">
          </va-slug-checker>
          <div v-if="isEdit && fullUrl" class="url-wrapper spacing-bottom--16">
            <a :href="fullUrl" target="_blank">
              {{ fullUrl }}
            </a>
            <CopyText display-icon :value="fullUrl"></CopyText>
          </div>

          <va-select-input
            v-if="ability.can('view', 'page type')"
            class="spacing-bottom--16 spacing-top--16"
            :source="'type'"
            v-model="form.type"
            :label="$t('resources.pages.page_type')"
            :choices="PAGE_TYPES"
            @change="pageTypeChanged">
          </va-select-input>
          <va-autocomplete-input
            class="spacing-bottom--16"
            required
            v-if="form.type === 2"
            :label="$t('resources.pages.page_category')"
            :placeholder="$t('resources.pages.page_category_placeholder')"
            v-model="form.category"
            source="category"
            reference="page_categories"
            :filter="resourcesFilter"
            ref="pageCategorySelect"
            item-value="@id"
            :min-chars="0"
            :items-per-page="5"
            showLoadMore>
          </va-autocomplete-input>

          <div class="d-flex" :class="{ 'spacing-top--16': !isEdit, 'spacing-bottom--16': isEdit }">
            <span class="pass-query-parameters-label">{{ $t("resources.pages.pass_query_parameters") }}</span>
            <v-switch
              v-model="form.passQueryParameters"
              class="ml-auto pass-query-parameters-switch"
              inset
              color="primary"
              :hide-details="'auto'"
              :ripple="false"></v-switch>
          </div>

          <div v-if="ability.can('view', 'ringba parameters')" class="d-flex spacing-bottom--16 spacing-top--16">
            <span class="pass-query-parameters-label">{{ $t("resources.pages.add_ringba_parameters") }}</span>
            <v-switch
              class="ml-auto pass-query-parameters-switch"
              v-model="showRingbaParameters"
              @change="handleRingbaSwitch"
              inset
              color="primary"
              :hide-details="'auto'"
              :ripple="false">
            </v-switch>
          </div>

          <v-expand-transition v-if="ability.can('view', 'ringba parameters')">
            <div class="ringba-block" v-if="showRingbaParameters">
              <va-array-input
                v-model="form.ringbaParameters"
                v-slot="props"
                :column-layout-without-drag="true"
                source="ringbaParameters"
                :label="false"
                :block-label="$t('resources.pages.ringba_block.block_title')"
                :hide-details="'auto'"
                :add-button-text="$t('resources.pages.ringba_block.button_title')">
                <va-text-input
                  v-model="form.ringbaParameters[props.index].tag"
                  class="spacing-vertical--6"
                  :label="$t('resources.pages.ringba_block.tag_label')"
                  :placeholder="$t('resources.pages.ringba_block.tag_placeholder')"
                  required
                  v-bind="props"
                  source="tag"></va-text-input>
                <v-text-field
                  :label="$t('resources.pages.ringba_block.number_label')"
                  class="spacing-vertical--6"
                  name="number"
                  :rules="[rules.required]"
                  :placeholder="$t('resources.pages.ringba_block.number_placeholder')"
                  v-model.lazy="form.ringbaParameters[props.index].number"
                  validate-on-blur
                  persistent-placeholder
                  hide-details="auto"
                  type="tel"
                  v-cleave="{
                    delimiters: ['+'],
                    blocks: [0, 11],
                    numericOnly: true,
                    value: form.ringbaParameters[props.index].number,
                  }"
                  required>
                  <template v-slot:message="{ message }">
                    {{ message }}
                  </template>
                </v-text-field>

                <div class="ringba-phone-input-caption spacing-vertical--8">
                  {{ $t("resources.pages.ringba_block.ringba-phone-caption") }}
                </div>
              </va-array-input>
            </div>
          </v-expand-transition>

          <div class="d-flex spacing-top--16" :class="{ 'spacing-bottom--16': isEdit }">
            <span class="pass-query-parameters-label">{{ $t("resources.pages.enable_lazy_loading") }}</span>
            <v-switch
              class="ml-auto pass-query-parameters-switch"
              v-model="form.lazyLoad"
              inset
              color="primary"
              :hide-details="'auto'"
              :ripple="false">
            </v-switch>
          </div>

          <!---Page Version -->
          <div v-if="isEdit" class="page-version-block">
            <div class="page-version-block__label">
              {{ $t("resources.pages.last_saved") }}
            </div>
            <div class="d-flex">
              <div class="page-version-block__info">{{ lastSavedPageVersionLabel }}</div>
              <div @click="handleDisplayVersionHistoryModal" class="page-version-block__view-history ml-auto">
                {{ $t("resources.pages.view_history") }}
              </div>
            </div>
          </div>

          <!-- Upload History -->
          <div v-if="isEdit" class="upload-history-block">
            <div class="d-flex align-center">
              <div class="upload-history-block__label">
                <span class="upload-history-block__title">
                  {{ $t("resources.pages.upload_status") }}
                </span>
                <v-chip
                  v-if="currentUploadedStatus && currentUploadedStatus.simplifiedStatus"
                  :color="currentUploadedStatus.backgroundColor"
                  class="upload-history-block__status-chip"
                  :style="{ color: currentUploadedStatus.color }">
                  {{ currentUploadedStatus.simplifiedStatus }}
                </v-chip>
              </div>
              <div @click="loadStatusHistory" class="upload-history-block__view-link ml-auto">
                <refresh-icon></refresh-icon><span class="ml-1">{{ $t("resources.pages.refresh") }}</span>
              </div>
            </div>

            <div class="upload-history-block__content">
              <div v-for="(status, index) in statusHistory" :key="status.id">
                <div v-if="index === 0">
                  <div class="d-flex align-center justify-space-between mt-2">
                    <div class="upload-history-block__first-line text-truncate">
                      {{
                        $t("resources.pages.current_version_label", {
                          date: status.createdAt,
                          name: status.user,
                        })
                      }}
                    </div>
                    <div
                      v-show="!showUploadedStatus"
                      @click="showUploadedStatus = true"
                      class="upload-history-block__view-link">
                      {{ $t("resources.pages.view_history") }}
                    </div>
                  </div>
                  <v-expand-transition>
                    <div v-show="showUploadedStatus">
                      <div class="upload-history-block__status">
                        {{ $t("resources.pages.actions") }}: {{ status.actions }}
                      </div>
                      <div>{{ $t("resources.pages.status") }}: {{ status.simplifiedStatus }}</div>
                    </div>
                  </v-expand-transition>
                </div>
                <v-expand-transition v-else>
                  <div v-show="showUploadedStatus" class="mt-4">
                    <div class="upload-history-block__first-line text-truncate">
                      {{
                        $t("resources.pages.current_version_label", {
                          date: status.createdAt,
                          name: status.user,
                        })
                      }}
                    </div>
                    <div class="upload-history-block__status">
                      {{ $t("resources.pages.actions") }}: {{ status.actions }}
                    </div>
                    <div>{{ $t("resources.pages.status") }}: {{ status.simplifiedStatus }}</div>
                  </div>
                </v-expand-transition>
              </div>
              <div class="d-flex justify-end mt-2" v-if="showUploadedStatus">
                <div @click="showUploadedStatus = false" class="upload-history-block__view-link">
                  {{ $t("resources.pages.show_less") }}
                </div>
              </div>
            </div>
          </div>
          <!---Page Version END -->
          <div v-if="ability.can('view', 'page ab test status') && form.displayAbTest" class="spacing-top--16">
            <div class="ab-test-label">
              {{ $t("resources.pages.ab_test") }}
              <div>
                <v-chip
                  v-if="form.displayAbTest.status === AB_TEST_STATUSES.ACTIVE"
                  class="status-chip active-chip"
                  color="info-120">
                  {{ $t("resources.ab_tests.active") }}
                </v-chip>
                <v-chip class="status-chip" v-if="form.displayAbTest.status === AB_TEST_STATUSES.DRAFT">
                  {{ $t("resources.ab_tests.draft") }}
                </v-chip>
                <v-chip
                  class="status-chip inactive-chip"
                  v-if="form.displayAbTest.status === AB_TEST_STATUSES.INACTIVE">
                  {{ $t("resources.ab_tests.inactive") }}
                </v-chip>
              </div>
            </div>
            <div class="ab-test-name" @click="openAbTest">
              {{ form.displayAbTest.name }}
            </div>
          </div>
        </v-card>

        <!-- Page Elements -->
        <v-card class="spacing-bottom--24">
          <v-card-title class="card-title">
            <v-icon class="card-icon">mdi-shape-square-plus</v-icon>
            {{ $t("resources.pages.page_elements_block_title") }}
          </v-card-title>
          <v-card-subtitle v-if="!form.domain" class="page-elements-card-subtitle">
            {{ $t("resources.pages.page_elements_block_subtitle") }}
          </v-card-subtitle>
          <va-multiple-expandable-input
            class="page-elements-panel-wrapper"
            v-if="form.domain"
            :items="pageElementItems">
            <!-- Header -->
            <template v-slot:[`header-action`]>
              <v-switch
                v-model="form.hasHeader"
                @change="showHeaderChange"
                color="primary"
                :ripple="false"
                hide-details
                inset></v-switch>
            </template>
            <template v-slot:[`header-content`]>
              <va-autocomplete-input
                class="page-element-autocomplete"
                v-model="form.header"
                ref="headersAutocomplete"
                :filter="resourcesFilter"
                :items-per-page="5"
                :min-chars="0"
                :label="$t('resources.pages.page_header')"
                :placeholder="$t('resources.pages.page_header_placeholder')"
                source="header"
                reference="headers"
                item-value="@id"
                :alternativeDropdownView="true"
                showLoadMore>
                <template v-slot:[`no-data`]>
                  <empty-select-search-list
                    resource-name="headers"
                    :empty-title="$t('resources.headers.no_headers_available')"
                    @click="createResourceHandler">
                  </empty-select-search-list>
                </template>
                <template #info>
                  <info-tooltip :info="$t('resources.pages.page_header_tooltip_text')"></info-tooltip>
                </template>
              </va-autocomplete-input>
            </template>

            <!-- Footer -->
            <template v-slot:[`footer-action`]>
              <v-switch
                v-model="form.hasFooter"
                @change="showFooterChange"
                color="primary"
                :ripple="false"
                hide-details
                inset></v-switch>
            </template>
            <template v-slot:[`footer-content`]>
              <va-autocomplete-input
                class="page-element-autocomplete"
                v-model="form.footer"
                ref="footersAutocomplete"
                :filter="resourcesFilter"
                :items-per-page="5"
                :min-chars="0"
                :label="$t('resources.pages.page_footer')"
                :placeholder="$t('resources.pages.page_footer_placeholder')"
                source="footer"
                reference="footers"
                item-value="@id"
                :alternativeDropdownView="true"
                showLoadMore>
                <template v-slot:[`no-data`]>
                  <empty-select-search-list
                    resource-name="footers"
                    :empty-title="$t('resources.footers.no_footers_available')"
                    @click="createResourceHandler">
                  </empty-select-search-list>
                </template>
                <template #info>
                  <info-tooltip :info="$t('resources.pages.page_footer_tooltip_text')"></info-tooltip>
                </template>
              </va-autocomplete-input>
            </template>

            <template v-slot:[`author-content`]>
              <va-autocomplete-input
                clearable
                v-model="form.author"
                source="author"
                reference="authors"
                :label="$t('resources.pages.author')"
                :placeholder="$t('resources.pages.author_placeholder')"
                item-value="@id"
                :alternativeDropdownView="true"
                :min-chars="0"
                :items-per-page="5"
                showLoadMore>
                <template v-slot:[`no-data`]>
                  <empty-select-search-list
                    resource-name="authors"
                    :empty-title="$t('generic.no_results_found')"
                    @click="createResourceHandler">
                  </empty-select-search-list>
                </template>
                <template #info>
                  <info-tooltip :info="$t('resources.pages.author_tooltip_text')"></info-tooltip>
                </template>
              </va-autocomplete-input>
            </template>

            <!-- Pop-up -->
            <template v-slot:[`popup-action`]>
              <v-switch
                v-model="form.showExitPopups"
                @change="showPopupChange"
                color="primary"
                :ripple="false"
                hide-details
                inset></v-switch>
            </template>
            <template v-slot:[`popup-content`]>
              <va-autocomplete-input
                class="page-element-autocomplete"
                v-model="form.modal"
                ref="modalsAutocomplete"
                :filter="resourcesFilter"
                :items-per-page="5"
                :min-chars="0"
                :label="$t('resources.pages.page_popup')"
                :placeholder="$t('resources.pages.page_popup_placeholder')"
                source="modal"
                reference="modals"
                item-text="name"
                item-value="@id"
                :alternativeDropdownView="true"
                showLoadMore>
                <template v-slot:[`no-data`]>
                  <empty-select-search-list
                    resource-name="popups"
                    :empty-title="$t('resources.popups.no_popups_available')"
                    @click="createResourceHandler">
                  </empty-select-search-list>
                </template>
                <template #info>
                  <info-tooltip :info="$t('resources.pages.page_popup_tooltip_text')"></info-tooltip>
                </template>
              </va-autocomplete-input>
            </template>

            <!-- Sidebar -->
            <template v-slot:[`sidebar-action`]>
              <v-switch
                v-model="form.hasSidebar"
                @change="showSidebarChange"
                color="primary"
                :ripple="false"
                hide-details
                inset></v-switch>
            </template>
            <template v-slot:[`sidebar-content`]>
              <va-autocomplete-input
                class="page-element-autocomplete"
                v-model="form.sidebar"
                ref="sidebarsAutocomplete"
                :filter="resourcesFilter"
                :items-per-page="5"
                :min-chars="0"
                :label="$t('resources.pages.page_sidebar')"
                :placeholder="$t('resources.pages.page_sidebar_placeholder')"
                source="sidebar"
                reference="sidebars"
                item-text="name"
                item-value="@id"
                :alternativeDropdownView="true"
                showLoadMore>
                <template v-slot:[`no-data`]>
                  <empty-select-search-list
                    resource-name="sidebars"
                    :empty-title="$t('resources.sidebars.no_sidebars_available')"
                    @click="createResourceHandler">
                  </empty-select-search-list>
                </template>
                <template #info>
                  <info-tooltip :info="$t('resources.pages.page_sidebar_tooltip_text')"></info-tooltip>
                </template>
              </va-autocomplete-input>
            </template>

            <!-- Favorites-block -->
            <template v-slot:[`favorites_block-action`]>
              <v-switch
                v-model="form.showFavoritesBlock"
                @change="showFavoritesBlockChange"
                color="primary"
                :ripple="false"
                hide-details
                inset></v-switch>
            </template>
            <template v-slot:[`favorites_block-content`]>
              <va-autocomplete-input
                class="page-element-autocomplete"
                v-model="form.favoritesBlock"
                ref="favoritesAutocomplete"
                :filter="resourcesFilter"
                :items-per-page="5"
                :min-chars="0"
                :label="$t('resources.pages.page_favorites_block')"
                :placeholder="$t('resources.pages.page_favorites_block_placeholder')"
                source="favoritesBlock"
                reference="favorites_blocks"
                item-text="name"
                item-value="@id"
                :alternativeDropdownView="true"
                showLoadMore>
                <template v-slot:[`no-data`]>
                  <empty-select-search-list
                    resource-name="favorites_blocks"
                    :empty-title="$t('resources.favorites_blocks.no_favorites_blocks_available')"
                    @click="createResourceHandler">
                  </empty-select-search-list>
                </template>
                <template #info>
                  <info-tooltip :info="$t('resources.pages.page_favorites_block_tooltip_text')"></info-tooltip>
                </template>
              </va-autocomplete-input>
            </template>

            <!-- SiteRunner Branding -->
            <template v-slot:[`siterunner_branding-action`]>
              <v-switch v-model="form.showBranding" color="primary" :ripple="false" hide-details inset></v-switch>
            </template>
            <template v-slot:[`siterunner_branding-content`]>
              <v-text-field
                hide-details
                persistent-placeholder
                v-model="form.brandingLink"
                :label="$t('resources.pages.page_branding_link_label')"
                :placeholder="$t('resources.pages.page_branding_link_placeholder')"></v-text-field>
            </template>
          </va-multiple-expandable-input>
        </v-card>

        <!-- Labels -->
        <v-card class="spacing-bottom--24">
          <v-card-title class="card-title label-card-title-wrapper">
            <div>
              <v-icon class="card-icon">mdi-label-multiple-outline</v-icon>
              {{ $t("resources.pages.labels") }}
            </div>
            <div v-if="!isLabelCreateActive" class="label-add-button" @click="isLabelCreateActive = true">
              <plus-icon class="fill--primary mr-1"></plus-icon>
              {{ $t("resources.pages.create_label") }}
            </div>
          </v-card-title>
          <v-card-subtitle
            v-if="labels.length === 0 && !isLabelCreateActive && !labelListLoading"
            class="labels-empty-subtitle">
            {{ $t("resources.pages.labels_empty") }}
          </v-card-subtitle>
          <div v-if="isLabelCreateActive">
            <v-form ref="labelForm" @submit.prevent="saveLabel" class="label-form">
              <v-text-field
                class="label-name-input"
                :label="$t('resources.pages.label_name')"
                :placeholder="$t('resources.pages.label_name_placeholder')"
                persistent-placeholder
                v-model="newLabel.name"
                :rules="[
                  v => !!v || $t('resources.pages.label_name_required'),
                  v => (v && v.length < 11) || $t('resources.pages.label_name_too_long'),
                ]" />
              <va-color-picker :title="$t('resources.pages.label_color')" v-model="newLabel.color" />
              <div class="label-create-button-wrapper">
                <v-btn @click="isLabelCreateActive = false" class="label-cancel-button" outlined>
                  {{ $t("resources.pages.cancel") }}
                </v-btn>
                <v-btn type="submit" color="primary">{{ $t("resources.pages.create") }}</v-btn>
              </div>
            </v-form>
          </div>
          <div v-else-if="labels.length > 0 || labelListLoading">
            <v-autocomplete
              class="label-primary-select"
              :label="$t('resources.pages.label_primary')"
              :placeholder="$t('resources.pages.label_primary_placeholder')"
              persistent-placeholder
              item-text="name"
              item-value="@id"
              append-icon="mdi-chevron-down"
              :items="labels"
              clearable
              v-model="form.label">
              <template #item="{ item }">
                <v-row class="label-select-item">
                  <v-avatar class="label-badge ml-4 mr-2" size="24" :color="item.color"></v-avatar>
                  <span class="label-title">{{ item.name }}</span>
                  <v-icon
                    v-show="false"
                    class="label-select-item__close-icon"
                    :key="item.name"
                    @click.prevent.stop="deleteLabel(item)"
                    size="16"
                    color="icon">
                    mdi-trash-can-outline
                  </v-icon>
                </v-row>
              </template>
              <template #selection="{ item }">
                <v-avatar class="label-badge mr-2" size="24" :color="item.color"></v-avatar> {{ item.name }}
              </template>
              <template #no-data>
                <div class="v-list-item theme--light">
                  <div class="v-list-item__content">
                    <div class="v-list-item__title">
                      {{ $t("resources.pages.no_labels_found") }}
                    </div>
                  </div>
                </div>
              </template>
            </v-autocomplete>
            <v-autocomplete
              :class="[
                'additional-label-autocomplete',
                additionalLabelSearch && additionalLabelSearch.length && 'v-autocomplete--search-added',
              ]"
              ref="additionalLabelAutoComplete"
              :label="$t('resources.pages.label_additional')"
              :placeholder="$t('resources.pages.label_additional_placeholder')"
              return-object
              item-text="name"
              :items="labels"
              :search-input.sync="additionalLabelSearch"
              append-icon="mdi-chevron-down"
              prepend-inner-icon="mdi-plus"
              multiple
              persistent-placeholder
              hide-details
              v-model="form.additionalLabels">
              <template #no-data>
                <div class="v-list-item theme--light">
                  <div class="v-list-item__content">
                    <div class="v-list-item__title">
                      {{ $t("resources.pages.no_labels_found") }}
                    </div>
                  </div>
                </div>
              </template>
              <template #selection> </template>
              <template #item="{ item }">
                <div
                  @mouseover="hoveredAdditionalLabel = item"
                  @mouseleave="hoveredAdditionalLabel = null"
                  class="additional-label-item-wrapper">
                  <div class="additional-label-item-inner-wrapper">
                    <div class="v-list-item__action">
                      <v-icon>{{
                        $refs["additionalLabelAutoComplete"].hasItem(item)
                          ? "mdi-checkbox-marked"
                          : "mdi-checkbox-blank-outline"
                      }}</v-icon>
                    </div>
                    <div class="v-list-item__content">
                      <div class="v-list-item__title">
                        <v-avatar class="label-badge mr-2" size="24" :color="item.color"></v-avatar>
                        {{ item.name }}
                      </div>
                    </div>
                  </div>
                  <v-icon
                    v-show="hoveredAdditionalLabel === item"
                    class="label-select-item__close-icon"
                    :key="item.name"
                    @click.prevent.stop="deleteLabel(item)"
                    size="16"
                    color="icon">
                    mdi-trash-can-outline
                  </v-icon>
                </div>
              </template>
            </v-autocomplete>
            <div
              :class="[
                'd-flex',
                'flex-wrap',
                'selection-chip__wrapper',
                form.additionalLabels.length ? 'selection-chip__wrapper--has-selection' : '',
              ]">
              <template v-for="item in form.additionalLabels">
                <v-chip
                  class="additional-label-chip"
                  :key="`${item.id}-key`"
                  :color="getLabelChipColor(item)"
                  :style="{ color: item.color }">
                  <span class="pr-2">
                    {{ item.name }}
                  </span>
                  <v-icon small @click="$refs['additionalLabelAutoComplete'].selectItem(item)">mdi-close</v-icon>
                </v-chip>
              </template>
            </div>
          </div>
        </v-card>

        <!-- Ringba tags -->
        <v-card v-if="ability.can('view', 'ringba user tag')" class="spacing-bottom--24">
          <v-card-title class="card-title ringba-tag-card-title-wrapper">
            <div>
              <v-icon class="card-icon">mdi-tag-outline</v-icon>
              {{ $t("resources.pages.ringba_tag") }}
            </div>
            <div v-if="!isRingbaTagCreateActive" class="ringba-tag-add-button" @click="isRingbaTagCreateActive = true">
              <plus-icon class="fill--primary mr-1"></plus-icon>
              {{ $t("resources.pages.create_ringba_tag") }}
            </div>
          </v-card-title>
          <v-card-subtitle
            v-if="ringbaTags.length === 0 && !isRingbaTagCreateActive && !ringbaTagListLoading"
            class="ringba-tags-empty-subtitle">
            {{ $t("resources.pages.ringba_tag_empty") }}
          </v-card-subtitle>
          <div v-if="isRingbaTagCreateActive">
            <v-form ref="ringbaTagForm" @submit.prevent="saveRingbaTag" class="ringba-tag-form">
              <v-text-field
                class="ringba-tag-name-input"
                :label="$t('resources.pages.ringba_tag_name')"
                :placeholder="$t('resources.pages.ringba_tag_name_placeholder')"
                persistent-placeholder
                v-model="newRingbaTag.name"
                :rules="[
                  v => !!v || $t('resources.pages.ringba_tag_name_required'),
                  v => (v && v.length <= 255) || $t('resources.pages.ringba_tag_name_too_long'),
                ]" />
              <div class="ringba-tag-create-button-wrapper">
                <v-btn @click="isRingbaTagCreateActive = false" class="ringba-tag-cancel-button" outlined>
                  {{ $t("resources.pages.cancel") }}
                </v-btn>
                <v-btn :disabled="!newRingbaTag.name" type="submit" color="primary">{{
                  $t("resources.pages.create")
                }}</v-btn>
              </div>
            </v-form>
          </div>
          <div v-else-if="ringbaTags.length > 0 || ringbaTagListLoading">
            <v-autocomplete
              class="ringba-tag-primary-select"
              :label="$t('resources.pages.ringba_tag_primary')"
              :placeholder="$t('resources.pages.ringba_tag_primary_placeholder')"
              persistent-placeholder
              item-text="name"
              item-value="@id"
              append-icon="mdi-chevron-down"
              :items="ringbaTags"
              clearable
              v-model="form.ringbaTag">
              <template #no-data>
                <div class="v-list-item theme--light">
                  <div class="v-list-item__content">
                    <div class="v-list-item__title">
                      {{ $t("resources.pages.no_ringba_tag_found") }}
                    </div>
                  </div>
                </div>
              </template>
              <template #item="{ item }">
                <div
                  @mouseover="hoveredRingbaTag = item"
                  @mouseleave="hoveredRingbaTag = null"
                  class="ringba-tag-item-wrapper">
                  <div class="ringba-tag-item-inner-wrapper">
                    <div class="v-list-item__content">
                      <div class="v-list-item__title">
                        {{ item.name }}
                      </div>
                    </div>
                  </div>
                  <v-icon
                    v-show="hoveredRingbaTag === item"
                    class="ringba-tag-select-item__close-icon"
                    :key="item.name"
                    @click.prevent.stop="deleteRingbaTag(item)"
                    size="16"
                    color="icon">
                    mdi-trash-can-outline
                  </v-icon>
                </div>
              </template>
            </v-autocomplete>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <required-field-text></required-field-text>
  </va-form>
</template>

<script>
  import definedAbility from "@/ability";
  // Mixins
  import crudFormMixin from "@/mixins/crudFormMixin";
  import ConnectedDomainsList from "@/mixins/connectedDomainsList";
  import providedDomain from "@/mixins/providedDomain";
  import Preview from "@/mixins/previewMethod";
  // Constants
  import { PAGE_STATUSES, PAGE_TYPES, AB_TEST_STATUSES, PAGE_UPLOAD_STATUSES, PAGE_HISTORY_STATUS } from "@/constant";
  // Components
  import VaSlugChecker from "@/components/custom/VaSlugChecker";
  import VaExpandableWrapper from "@/components/custom/VaExpandableWrapper";
  import VaImageDragAndDropInput from "@/components/custom/VaImageDragAndDropInput";
  import VaMultipleExpandableInput from "@/components/custom/VaMultipleExpandableInput";
  import CodeInput from "@/components/custom/CodeInput";
  import InfoTooltip from "@/components/custom/InfoTooltip";
  import RequiredFieldText from "@/components/custom/RequiredFieldText";
  import EmptySelectSearchList from "@/components/custom/EmptySelectSearchList.vue";
  import Modal from "@/components/custom/Modal";
  import PlusIcon from "@/components/icons/PlusIcon";
  import RefreshIcon from "@/components/icons/RefreshIcon";
  import VaJsonInput from "@/components/custom/VaJsonInput";
  import CopyText from "@/components/custom/CopyText";
  import VaColorPicker from "@/components/custom/VaColorPicker";
  // Utils
  import { parseToHtml } from "@/utility/parseToHtml";
  import { cleanupTinymceContent } from "@/utility/cleanupTinymceContent";
  import { serializeDynamicComponents } from "@/assets/utilities/tinymce/visualization";
  import { convertAssetsPathToAbsoluteLinks, convertAssetsPathToRelativeLinks } from "@/utility/convertAssetsPaths";
  import { setLazyLoad, removeLazyLoad } from "@/utility/setLazyLoad";
  import { getFullDateAndTime } from "@core/utils/filter";
  import { urlValidation } from "@/utility/urlValidation";
  import { formErrorMessage } from "@/utility/formErrorMessage";
  // Data
  import statesData from "@/assets/data/states";
  import citiesData from "@/assets/data/cities";
  import countiesData from "@/assets/data/counties";
  import countriesData from "@/assets/data/countries";
  import devicesData from "@/assets/data/devices";
  // directives
  import cleave from "@/directives/cleave";

  export default {
    name: "PageForm",
    components: {
      VaSlugChecker,
      VaExpandableWrapper,
      VaImageDragAndDropInput,
      VaMultipleExpandableInput,
      InfoTooltip,
      CodeInput,
      RequiredFieldText,
      EmptySelectSearchList,
      Modal,
      PlusIcon,
      VaJsonInput,
      CopyText,
      VaColorPicker,
      RefreshIcon,
    },
    mixins: [crudFormMixin, ConnectedDomainsList, providedDomain, Preview],
    directives: {
      cleave,
    },
    props: {
      // Determines if form is used while editing
      isEdit: {
        type: Boolean,
        default: false,
      },
      page: {
        type: Object,
        default: () => {},
      },
      needModal: {
        type: Boolean,
        default: false,
      },
      redirectRouteName: {
        type: String,
        default: "",
      },
      redirectRouteParams: {
        type: Object,
        default: () => ({}),
      },
    },
    data() {
      return {
        ability: definedAbility(),
        versionKey: null,
        PAGE_TYPES,
        PAGE_STATUSES,
        AB_TEST_STATUSES,
        // Might have to set default values for ALL the fields here (!)
        form: {},
        // Page versioning
        versionsOptions: [],
        pageVersions: [],
        lastSavedPageVersionLabel: "",
        selectedVersion: null,
        displayVersionHistoryModal: false,
        changePageVersionLoading: false,
        imageUploadQueue: [],
        excerptImageUploadQueue: [],
        alternativeContentImageUploadQueue: [],
        dynamicContentImageUploadQueue: [],
        preHeaderImageUploadQueue: [],
        /**
         * When previewing a page, we always have to send the latest page slug, otherwise preview will fail.
         * - Let's say user restores some old version of a page where slug was different from the latest one,
         * preview would fail in that case;
         * Only used for previewing (!)
         */
        latestPageSlug: "",
        imagesBlockVisible: false,
        codeBlockVisible: false,
        preHeaderVisible: false,
        changedStatus: null,
        successRedirectLink: {},
        displayDeleteLabelModal: false,
        labelToDelete: {},
        dynamicContentVisible: false,
        displayDeleteDynamicContentModal: false,
        dynamicContent: "",
        // Alternative content properties
        displayDeleteAlternativeContentModal: false,
        alternativeContentVisible: false,
        alternativeContentConditions: {
          checkUniqueClickId: false,
          enableUTMParameters: false,
          utmSource: "",
          utmMedium: "",
          utmCampaign: "",
          utmTerm: "",
          utmContent: "",
          selectedStates: [],
          selectedCities: [],
          selectedCounties: [],
          selectedCountries: [],
          selectedDevices: [],
        },
        showUploadedStatus: false,
        statusHistory: [],
        labelStatusLoading: false,
        alternativeContent: "",
        statesData: statesData,
        citiesData: citiesData,
        countiesData: countiesData,
        showDeleteIcon: false,
        countriesData: countriesData,
        newLabel: {},
        labels: [],
        isLabelCreateActive: false,
        additionalLabelSearch: "",
        hoveredAdditionalLabel: null,
        labelListLoading: false,
        formErrors: {},
        devices: devicesData,
        showRingbaParameters: false,
        createPageAlternativeContent: false,
        pageAlternativeContent: {
          "@id": "page-alternative-content",
          name: this.$t("resources.pages.alternative_content_create"),
          hidden: true,
          skipBackendGet: true,
        },
        rules: {
          required: value => !!value || this.$t("generic.validation.number_to_replace"),
          url: value => urlValidation(value) || this.$t("generic.validation.url"),
        },
        ringbaTags: [],
        newRingbaTag: {},
        isRingbaTagCreateActive: false,
        hoveredRingbaTag: null,
        displayDeleteRingbaTagModal: false,
        ringbaTagListLoading: false,
      };
    },
    computed: {
      currentUploadedStatus() {
        const simplifiedStatus = this.statusHistory[0]?.simplifiedStatus;

        if (!simplifiedStatus) {
          return null;
        }

        if (simplifiedStatus === PAGE_UPLOAD_STATUSES.ERROR) {
          return {
            simplifiedStatus,
            backgroundColor: "rgba(255, 76, 81, 0.12)",
            color: "#FF7074",
          };
        }

        if (simplifiedStatus === PAGE_UPLOAD_STATUSES.SUCCESS) {
          return {
            simplifiedStatus,
            backgroundColor: "rgba(34, 185, 77, 0.12)",
            color: "#22B94D",
          };
        }

        return {
          simplifiedStatus,
          backgroundColor: "rgba(13, 110, 253, 0.12)",
          color: "#0D6EFD",
        };
      },
      alternativeContentConditionsJson: {
        get() {
          if (this.alternativeContentConditions.enableUTMParameters) {
            return this.alternativeContentConditions;
          } else {
            return {
              selectedStates: this.alternativeContentConditions.selectedStates,
              selectedCities: this.alternativeContentConditions.selectedCities,
              selectedCounties: this.alternativeContentConditions.selectedCounties,
              selectedCountries: this.alternativeContentConditions.selectedCountries,
              selectedDevices: this.alternativeContentConditions.selectedDevices,
            };
          }
        },
        set(value) {
          if (this.alternativeContentConditions.enableUTMParameters) {
            this.alternativeContentConditions.utmSource = value.utmSource;
            this.alternativeContentConditions.utmMedium = value.utmMedium;
            this.alternativeContentConditions.utmCampaign = value.utmCampaign;
            this.alternativeContentConditions.utmTerm = value.utmTerm;
            this.alternativeContentConditions.utmContent = value.utmContent;
            this.alternativeContentConditions.checkUniqueClickId = value.checkUniqueClickId;
          }
          this.alternativeContentConditions.selectedStates = value.selectedStates || [];
          this.alternativeContentConditions.selectedCities = value.selectedCities || [];
          this.alternativeContentConditions.selectedCounties = value.selectedCounties || [];
          this.alternativeContentConditions.selectedCountries = value.selectedCountries || [];
          this.alternativeContentConditions.selectedDevices = value.selectedDevices || [];
        },
      },
      customRedirectRoute() {
        return this.isEdit
          ? null
          : {
              name: "pages_list",
            };
      },
      fullUrl() {
        const domainItem = this.domainsList.find(x => x.value === this.form.domain);

        if (!domainItem || !this.form.slug) {
          return "";
        }

        if (this.form.slug === "/") {
          return "https://" + domainItem.text + "/";
        }

        return "https://" + domainItem.text + "/" + this.form.slug + "/";
      },
      /**
       * Used when filtering autocomplete inputs' results.
       * Only the results for current domain have to be displayed
       */
      resourcesFilter() {
        return this.form.domain ? { domain: this.form.domain.replace("/api/domains/", "") } : null;
      },
      pageElementItems() {
        const items = [
          {
            title: "Header",
            slotName: "header",
            hidden: false,
            expanded: this.form.hasHeader,
          },
          {
            title: "Footer",
            slotName: "footer",
            hidden: false,
            expanded: this.form.hasFooter,
          },
          {
            title: "Author",
            slotName: "author",
            hidden: false,
            expanded: true,
            actionHidden: true,
          },
          {
            title: "Pop-up",
            slotName: "popup",
            hidden: false,
            expanded: this.form.showExitPopups,
          },
          {
            title: "Sidebar",
            slotName: "sidebar",
            hidden: false,
            expanded: this.form.hasSidebar,
          },
          {
            title: "Favorites block",
            slotName: "favorites_block",
            hidden: false,
            expanded: this.form.showFavoritesBlock,
          },
          {
            title: "SiteRunner branding",
            slotName: "siterunner_branding",
            hidden: !this.ability.can("view", "siterunner branding"),
            expanded: this.form.showBranding,
          },
        ];

        const filteredItems = items.filter(item => {
          // If page type is "Form"
          if (this.form.type === 3) {
            return item.slotName !== "header";
          }

          return item;
        });

        return filteredItems;
      },
      requiredFormFields() {
        let fields = ["headline", "mainContent", "domain", "slug"];
        if (this.form.type === 2) {
          fields.push("category");
        }

        return fields;
      },
      statusObject() {
        return PAGE_STATUSES.find(item => item.value === this.form.status);
      },
    },
    async beforeMount() {
      const isDuplicateForm = !this.isEdit && !!this.page;

      if (this.page?.versions?.length) {
        this.pageVersions = Object.assign([], this.page.versions);
        this.setVersionItems(this.pageVersions, this.page.versions[0]);
        // Set latest page version label
        this.setLastSavedLabel(this.page.versions[0]);
      }

      // Transform some of the form values. (Comes back as object from back-end, but has to be transformed to IRI)
      this.form = this.page || {};
      this.form.domain = this.page?.domain?.["@id"];
      this.form.modal = this.page?.modal?.["@id"];
      this.form.label = this.page?.label?.["@id"];
      this.form.ringbaTag = this.page?.ringbaTag?.["@id"];
      this.form.sidebar = this.page?.sidebar?.["@id"];
      this.form.header = this.page?.header?.["@id"];
      this.form.footer = this.page?.footer?.["@id"];
      this.form.favoritesBlock = this.page?.favoritesBlock?.["@id"];
      this.form.featuredImage = this.page?.featuredImage?.["@id"];
      this.form.featuredImageCaption = this.page?.featuredImageCaption || "";
      this.form.thumbnailImage = this.page?.thumbnailImage?.["@id"];
      this.form.category = this.page?.category?.["@id"];
      this.form.author = this.page?.author?.["@id"];
      this.form.hasHeader = this.page?.hasHeader || false;
      this.form.hasFooter = this.page?.hasFooter || false;
      this.form.hasSidebar = this.page?.hasSidebar || false;
      this.form.showExitPopups = this.page?.showExitPopups || false;
      this.form.showFavoritesBlock = this.page?.showFavoritesBlock || false;
      this.form.passQueryParameters = this.page?.passQueryParameters || false;
      this.form.lazyLoad = this.page?.lazyLoad || false;
      this.form.additionalLabels = this.page?.additionalLabels || [];

      this.form.ringbaParameters = this.page?.ringbaParameters || [];
      this.showRingbaParameters = this.page?.ringbaParameters.length || false;

      this.form.excerpt = convertAssetsPathToAbsoluteLinks(this.page?.excerpt);
      this.form.preHeader = convertAssetsPathToAbsoluteLinks(this.page?.preHeader);
      this.updateMainContent(convertAssetsPathToAbsoluteLinks(this.page?.mainContent));
      // Alternative content
      this.alternativeContentVisible = !!this.page?.sharedAlternativeContent || !!this.page?.loneAlternativeContent;

      if (this.page?.loneAlternativeContent) {
        this.updateAlternativeContent(convertAssetsPathToAbsoluteLinks(this.page?.loneAlternativeContent?.content));
        this.alternativeContentConditions = this.page?.loneAlternativeContent?.conditionals || {
          ...this.alternativeContentConditions,
        };

        this.form.loneAlternativeContent = this.page?.loneAlternativeContent || {};
        this.form.loneAlternativeContent.headline = this.page?.loneAlternativeContent?.headline || "";
        this.form.sharedAlternativeContent = this.pageAlternativeContent["@id"];
        this.createPageAlternativeContent = true;
      } else if (this.page?.sharedAlternativeContent) {
        this.form.sharedAlternativeContent = this.page?.sharedAlternativeContent["@id"];
        this.form.loneAlternativeContent = {};
        this.form.loneAlternativeContent.headline = "";
      }

      this.dynamicContentVisible = !!this.page?.calendar || !!this.page?.calendarContent;
      this.form.calendar = this.page?.calendar?.["@id"];
      this.form.calendarContent = this.page?.calendarContent || {};
      this.updateDynamicContent(convertAssetsPathToAbsoluteLinks(this.page?.calendarContent?.content));

      this.form.serverSideContentSplit = this.page?.serverSideContentSplit || false;

      this.form.template = this.page?.template === "main" ? "default" : this.page?.template;
      // Default page status - 'draft'. When duplicating, always set page status to 'draft'
      this.form.status = isDuplicateForm ? 1 : this.page?.status || 1;
      // Initial (default) page type lander;
      this.form.type = this.page?.type || 1;
      if (this.page && !this.isEdit) {
        this.form.slug = this.page?.slug + "-copy";
      }

      if (!this.isEdit) {
        delete this.form["@context"];
        delete this.form["@id"];
        delete this.form["@type"];
      }

      this.latestPageSlug = this.page?.slug || "";

      this.imagesBlockVisible = !!this.page?.featuredImage || !!this.page?.thumbnailImage;
      this.codeBlockVisible = !!this.page?.headCustomCode || !!this.page?.bodyCustomCode;

      if (!this.ability.can("view", "page type")) {
        this.form.type = 1;
        this.form.category = null;
      }

      this.form.headCustomCode = this.page?.headCustomCode || "";
      this.form.bodyCustomCode = this.page?.bodyCustomCode || "";

      this.formOriginalState = JSON.stringify(this.form);

      this.loadLabels();
      if (this.ability.can("view", "ringba user tag")) {
        this.loadRingbaTags();
      }

      if (this.page?.id) {
        this.loadStatusHistory();
      }
    },
    mounted() {
      if (this.$refs.additionalLabelAutoComplete?.$el) {
        this.$refs.additionalLabelAutoComplete.$el
          .querySelector(".v-select__selections")
          .setAttribute("data-placeholder", this.$t("resources.pages.label_additional_placeholder"));
      }
    },
    methods: {
      async saveRingbaTag() {
        if (!this.$refs.ringbaTagForm.validate()) {
          return;
        }
        try {
          const response = await this.$admin.dataProvider.create("ringba_tags", {
            data: {
              name: this.newRingbaTag.name,
            },
          });
          this.newRingbaTag = {};
          this.$admin.toast.success(this.$t("resources.pages.ringba_tag_create_success"));
          this.ringbaTags.push(response.data);
          this.ringbaTags.sort((a, b) => a.name.localeCompare(b.name));
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.isRingbaTagCreateActive = false;
        }
      },
      async loadRingbaTags() {
        this.ringbaTagListLoading = true;
        try {
          const response = await this.$admin.dataProvider.getList("ringba_tags", {
            disablePagination: true,
            sort: [{ by: "name", desc: false }],
          });
          this.ringbaTags = response.data;
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.ringbaTagListLoading = false;
        }
      },
      deleteRingbaTag(item) {
        this.displayDeleteRingbaTagModal = true;
        this.ringbaTagToDelete = item;
      },
      async confirmDeleteRingbaTag() {
        try {
          await this.$admin.dataProvider.customHttpCall("DELETE", `ringba_tags/${this.ringbaTagToDelete.id}`);
          this.$admin.toast.success(this.$t("resources.pages.ringba_tag_deleted_successfully"));
        } catch (error) {
          this.$admin.toast.error(this.$t("resources.pages.ringba_tag_delete_error"));
        } finally {
          this.ringbaTags = this.ringbaTags.filter(item => item["@id"] !== this.ringbaTagToDelete["@id"]);
          if (this.form.ringbaTag && this.form.ringbaTag === this.ringbaTagToDelete["@id"]) {
            this.form.ringbaTag = null;
          }
        }
        this.displayDeleteRingbaTagModal = false;
        this.ringbaTagToDelete = {};
      },
      cancelDeleteRingbaTag() {
        this.ringbaTagToDelete = {};
        this.displayDeleteRingbaTagModal = false;
      },
      getSelectedSharedAlternativeContent() {
        const item = this.$refs.alternativeContentAutocomplete?.items?.find(
          item => item["@id"] === this.form.sharedAlternativeContent,
        );

        return item;
      },
      openAbTest(e) {
        if (e.ctrlKey || e.metaKey) {
          window.open(
            this.$router.resolve({ name: "ab_tests_edit", params: { id: this.form.displayAbTest?.id } }).href,
            "_blank",
          );

          return;
        }
        if (this.needModal) {
          this.$emit("changeRedirectRouteName", "ab_tests_edit");
          this.$emit("changeRedirectRouteParams", { id: this.form.displayAbTest?.id });
          this.$emit("openModal", true);
        } else {
          this.$router.push({
            name: "ab_tests_edit",
            params: { id: this.form.displayAbTest?.id },
          });
        }
      },
      openGlobalAlternativeContent() {
        if (this.needModal) {
          this.$emit("changeRedirectRouteName", "alternative_contents_edit");
          this.$emit("changeRedirectRouteParams", { id: this.getSelectedSharedAlternativeContent()?.id });
          this.$emit("openModal", true);
        } else {
          this.$router.push({
            name: "alternative_contents_edit",
            params: { id: this.getSelectedSharedAlternativeContent()?.id },
          });
        }
      },
      sharedContentHasUrl() {
        const item = this.getSelectedSharedAlternativeContent();

        return !item?.urlPlaceholder;
      },
      alternativeContentEmpty() {
        const filteredItems = this.$refs.alternativeContentAutocomplete?.items?.filter(
          item => item["@id"] !== this.pageAlternativeContent["@id"],
        );
        if (
          (filteredItems?.length === 0 && !this.$refs.alternativeContentAutocomplete?.search) ||
          (this.pageAlternativeContent.name.includes(this.$refs.alternativeContentAutocomplete?.search) &&
            this.form.sharedAlternativeContent !== this.pageAlternativeContent["@id"] &&
            filteredItems?.length === 0)
        ) {
          return true;
        } else return false;
      },
      onCreateAlternativeContentChange(item) {
        if (item === this.pageAlternativeContent["@id"] && !this.createPageAlternativeContent) {
          this.createPageAlternativeContent = true;
          this.form.sharedAlternativeContent = this.pageAlternativeContent["@id"];
          this.$refs.alternativeContentAutocomplete.$refs.autocomplete.blur();
          this.form.loneAlternativeContent = {};
        } else if (item !== this.pageAlternativeContent["@id"]) {
          this.createPageAlternativeContent = false;
        }
      },
      async saveLabel() {
        if (!this.$refs.labelForm.validate()) {
          return;
        }
        try {
          const response = await this.$admin.dataProvider.create("labels", {
            data: {
              name: this.newLabel.name,
              color: this.newLabel.color ?? "#FFFFFF",
            },
          });
          this.newLabel = {};
          this.$admin.toast.success(this.$t("resources.pages.label_create_success"));
          this.labels.push(response.data);
          this.labels.sort((a, b) => a.name.localeCompare(b.name));
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.isLabelCreateActive = false;
        }
      },
      getLabelChipColor(item) {
        let hex = item.color;
        let r = parseInt(hex.slice(1, 3), 16);
        let g = parseInt(hex.slice(3, 5), 16);
        let b = parseInt(hex.slice(5, 7), 16);

        return "rgba(" + r + ", " + g + ", " + b + ", " + 0.12 + ")";
      },
      async loadLabels() {
        this.labelListLoading = true;
        try {
          const response = await this.$admin.dataProvider.getList("labels", {
            disablePagination: true,
            sort: [{ by: "name", desc: false }],
          });
          this.labels = response.data;
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.labelListLoading = false;
        }
      },
      async loadStatusHistory() {
        this.labelStatusLoading = true;
        try {
          const { data } = await this.$admin.dataProvider.customHttpCall(
            "GET",
            `pages/${this.page.id}/upload_statuses`,
          );
          if (data.length > 0) {
            this.statusHistory = data.map(item => this.formatHistoryData(item));
          }
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.labelStatusLoading = false;
        }
      },
      formatHistoryData(item) {
        let user = "";
        if (item?.invokedBy?.firstName) {
          user = item.invokedBy.firstName;
        }

        if (item?.invokedBy?.lastName) {
          user += " " + item.invokedBy.lastName;
        }

        if (user.trim() === "") {
          user = PAGE_HISTORY_STATUS.UNKNOWN;
        }

        return {
          ...item,
          user,
          simplifiedStatus: item?.simplifiedStatus ?? "",
          actions: item.actions?.map(action => action.split("_").join(" ")).join(", ") || "",
          createdAt: getFullDateAndTime(item.createdAt),
        };
      },
      deleteLabel(item) {
        this.displayDeleteLabelModal = true;
        this.labelToDelete = item;
      },
      async confirmDeleteLabel() {
        try {
          await this.$admin.dataProvider.customHttpCall("DELETE", `labels/${this.labelToDelete.id}`);
          this.$admin.toast.success(this.$t("resources.pages.label_deleted_successfully"));
        } catch (error) {
          this.$admin.toast.error(this.$t("resources.pages.label_delete_error"));
        } finally {
          this.labels = this.labels.filter(item => item["@id"] !== this.labelToDelete["@id"]);
          if (this.form.label && this.form.label === this.labelToDelete["@id"]) {
            this.form.label = null;
          }
          this.form.additionalLabels = this.form.additionalLabels.filter(
            item => item["@id"] !== this.labelToDelete["@id"],
          );
        }
        this.displayDeleteLabelModal = false;
        this.labelToDelete = {};
      },
      cancelDeleteLabel() {
        this.labelToDelete = {};
        this.displayDeleteLabelModal = false;
      },
      confirmAlternativeContentDelete() {
        this.alternativeContentVisible = false;
        this.displayDeleteAlternativeContentModal = false;
        this.createPageAlternativeContent = false;
        this.form.sharedAlternativeContent = null;
        this.form.loneAlternativeContent = null;
        // eslint-disable-next-line vue/no-mutating-props
        this.page.sharedAlternativeContent = null;
        this.alternativeContent = "";
        this.alternativeContentConditions = {
          checkUniqueClickId: false,
          enableUTMParameters: false,
          utmSource: "",
          utmMedium: "",
          utmCampaign: "",
          utmTerm: "",
          utmContent: "",
          selectedStates: [],
          selectedCities: [],
          selectedCounties: [],
          selectedCountries: [],
          selectedDevices: [],
        };
      },
      cancelAlternativeContentDelete() {
        this.displayDeleteAlternativeContentModal = false;
      },
      confirmDynamicContentDelete() {
        this.form.calendar = null;
        if (this.$refs.form?.formState?.item) {
          this.$refs.form.formState.item.calendar = null;
        }
        this.form.calendarContent = {};
        this.dynamicContent = "";
        this.dynamicContentVisible = false;
        this.displayDeleteDynamicContentModal = false;
      },
      cancelDynamicContentDelete() {
        this.displayDeleteDynamicContentModal = false;
      },
      enableUTMParametersChange(val) {
        if (!val) {
          this.alternativeContentConditions.utmSource = "";
          this.alternativeContentConditions.utmMedium = "";
          this.alternativeContentConditions.utmCampaign = "";
          this.alternativeContentConditions.utmTerm = "";
          this.alternativeContentConditions.utmContent = "";
          this.alternativeContentConditions.checkUniqueClickId = false;
        }
      },
      finishedSaving() {
        this.$emit("finishedSaving");
      },
      handleAfterSave(data) {
        if (this.$refs?.form?.formState?.errors) {
          this.formErrors = this.$refs.form.formState.errors;
          if (this.formErrors?.serverSideContentSplit?.[0]) {
            this.$admin.toast.error(this.formErrors.serverSideContentSplit[0]);
          }
        }
        if (data) {
          this.pageVersions = data.versions;
          if (this.pageVersions.length) {
            this.setVersionItems(this.pageVersions, this.pageVersions[0]);
            this.setLastSavedLabel(this.pageVersions[0]);
          }
          this.latestPageSlug = data.slug;
          this.$emit("slugUpdated", data.slug);

          if (this.changedStatus) {
            this.changedStatus = null;
            this.form.status = data.status;
            this.$emit("statusChanged", data.status);
          }
        }
        this.afterSave();
      },
      setLastSavedLabel(pageVersion) {
        this.lastSavedPageVersionLabel = pageVersion?.updatedBy
          ? this.$t("resources.pages.current_version_label", {
              date: getFullDateAndTime(pageVersion.date),
              name: pageVersion.updatedBy,
            })
          : getFullDateAndTime(pageVersion.date);
      },
      /**
       * Set items for versions select in the modal.
       * Current version is required so that we can add "in use" label and disable the selection.
       * @param {Array} versions
       * @param {Object} currentVersion
       */
      setVersionItems(versions, currentVersion) {
        this.versionsOptions = versions.map(versionItem => {
          return {
            text: versionItem?.updatedBy
              ? this.$t("resources.pages.saved_by_full", {
                  date: getFullDateAndTime(versionItem.date),
                  name: versionItem?.updatedBy,
                })
              : getFullDateAndTime(versionItem.date),
            value: versionItem.version,
            disabled: versionItem.version === currentVersion.version,
          };
        });
      },
      createResourceHandler(resourceName) {
        const createResourceRoute = this.$router.resolve({
          name: `${resourceName}_create`,
        });

        window.open(createResourceRoute.href, "_blank");
      },
      // Manual animation is needed because tinyMCE container is dynamic height
      animatePreHeader() {
        let el = this.$refs.preHeaderTextField.$el;
        let height = el.scrollHeight;
        if (this.preHeaderVisible) {
          el.style.height = height + "px";
          var transitionend = function () {
            el.removeEventListener("transitionend", transitionend);
            el.style.height = "auto";
          };
          el.addEventListener("transitionend", transitionend);
        } else {
          let transition = el.style.transition;
          el.style.transition = "";
          requestAnimationFrame(function () {
            el.style.height = height + "px";
            el.style.transition = transition;

            requestAnimationFrame(function () {
              el.style.height = 0 + "px";
            });
          });
        }
      },
      prepareFormBeforeSave(data) {
        const lazyLoadEnabled = this.form.lazyLoad;

        if (this.alternativeContentVisible) {
          data.alternativeContent = {};

          if (
            this.form.sharedAlternativeContent &&
            this.form.sharedAlternativeContent !== this.pageAlternativeContent["@id"]
          ) {
            data.loneAlternativeContent = null;
            data.sharedAlternativeContent = this.form.sharedAlternativeContent;
          } else {
            if (this.alternativeContent) {
              const htmlElementFromContent = convertAssetsPathToRelativeLinks(this.alternativeContent);

              cleanupTinymceContent(htmlElementFromContent);

              if (lazyLoadEnabled) {
                setLazyLoad(htmlElementFromContent, 2);
              } else {
                removeLazyLoad(htmlElementFromContent);
              }

              serializeDynamicComponents(htmlElementFromContent);
              data.loneAlternativeContent.content = htmlElementFromContent.body.innerHTML;
            } else {
              data.loneAlternativeContent.content = "";
            }

            data.sharedAlternativeContent = null;
            data.sharedAlternativeContentUrl = null;
            data.loneAlternativeContent.conditionals = this.alternativeContentConditions;
            data.loneAlternativeContent.headline = this.form.loneAlternativeContent?.headline || "";
          }
        } else {
          data.loneAlternativeContent = null;
          data.sharedAlternativeContent = null;
          data.sharedAlternativeContentUrl = null;
        }

        if (this.dynamicContentVisible) {
          data.calendarContent = {};

          if (this.dynamicContent) {
            const htmlElementFromContent = convertAssetsPathToRelativeLinks(this.dynamicContent);

            cleanupTinymceContent(htmlElementFromContent);

            if (lazyLoadEnabled) {
              setLazyLoad(htmlElementFromContent, 2);
            } else {
              removeLazyLoad(htmlElementFromContent);
            }

            serializeDynamicComponents(htmlElementFromContent);
            data.calendarContent.content = htmlElementFromContent.body.innerHTML;
          } else {
            data.calendarContent.content = "";
          }

          data.calendarContent.headline = this.form.calendarContent?.headline || "";
        } else {
          data.calendarContent = null;
        }

        if (data.mainContent) {
          const htmlElementFromContent = convertAssetsPathToRelativeLinks(data.mainContent);

          cleanupTinymceContent(htmlElementFromContent);

          if (lazyLoadEnabled) {
            setLazyLoad(htmlElementFromContent, 2);
          } else {
            removeLazyLoad(htmlElementFromContent);
          }

          data.mainContent = htmlElementFromContent.body.innerHTML;
        }

        if (data.preHeader) {
          const htmlElementFromContent = convertAssetsPathToRelativeLinks(data.preHeader);

          cleanupTinymceContent(htmlElementFromContent);

          data.preHeader = htmlElementFromContent.body.innerHTML;
        }

        if (data.excerpt) {
          const htmlElementFromContent = convertAssetsPathToRelativeLinks(data.excerpt);

          cleanupTinymceContent(htmlElementFromContent);

          data.excerpt = htmlElementFromContent.body.innerHTML;
        }

        if (data.additionalLabels) {
          data.additionalLabels = data.additionalLabels.map(label => label["@id"]);
        }

        /**
         * Right before we save a page, we have to serialize the comments widget content for it to become
         * a Vue template, which can then be rendered in twig templates
         */
        const content = parseToHtml(data.mainContent);
        serializeDynamicComponents(content);

        data.mainContent = content.body.innerHTML;

        /**
         * If the default sidebar of the domain is the same as the currently set sidebar,
         * We don't send it in the request.
         * The reason being that if we send the default sidebar of the domain in the Page Edit request,
         * the page will be saved in the database, and it will have sidebar ID set to the CURRENT default sidebar ID.
         * Now let's say we change the default sidebar to some other sidebar, the sidebar ID of the edited page
         * will remain as the OLD default domain sidebar ID, which would be wrong.
         */
        if (this.defaultSidebarData?.["@id"] === data.sidebar) {
          data.sidebar = null;
        }

        // Intermediate status change to prevent status change display if save fails
        if (this.changedStatus) {
          data.status = this.changedStatus;
        }

        return data;
      },
      async domainChanged() {
        /**
         * entities - modals/sidebars
         * Only domain-specific entities can be selected, that's why we have to clear form entities if domain changes
         * Also, clear cached autocomplete search items, because some of them might belong to another domain. If not cleared,
         * user would be able select an entity which belongs to another domain
         */
        this.form.modal = null;
        this.form.label = null;
        this.form.sidebar = null;
        this.form.header = null;
        this.form.footer = null;
        this.form.favoritesBlock = null;
        this.form.category = null;
      },
      pageTypeChanged(val) {
        // If page is a lander type
        if (val === 1) {
          this.form.category = null;
          // Remove sidebar
          this.form.hasSidebar = false;
          this.form.sidebar = null;
          // Remove favorites block
          this.form.showFavoritesBlock = false;
          this.form.favoritesBlock = null;
          this.form.thumbnailImage = null;
        }

        // If page is a Form page
        if (val === 3) {
          this.form.hasHeader = false;
          this.form.header = null;
        }
      },
      updateFeaturedImage(newValue) {
        if (newValue === "") {
          this.form.featuredImage = null;
        } else {
          this.form.featuredImage = newValue["@id"];
        }
      },
      updateThumbnailImage(newValue) {
        if (newValue === "") {
          this.form.thumbnailImage = null;
        } else {
          this.form.thumbnailImage = newValue["@id"];
        }
      },
      updatePreHeader(newValue) {
        this.form.preHeader = newValue;
      },
      updateExcerptText(newValue) {
        this.form.excerpt = newValue;
      },
      updateMainContent(newValue) {
        let htmlElementFromContent = parseToHtml(newValue);

        htmlElementFromContent.querySelectorAll("img").forEach(e => {
          // If the image is not from external source
          if (e.dataset.image !== "") {
            // eslint-disable-next-line no-self-assign
            e.src = e.src;
          }
        });

        this.form.mainContent = htmlElementFromContent.body.innerHTML;
      },
      updateAlternativeContent(newValue) {
        let htmlElementFromContent = parseToHtml(newValue);

        htmlElementFromContent.querySelectorAll("img").forEach(e => {
          // If the image is not from external source
          if (e.dataset.image !== "") {
            // eslint-disable-next-line no-self-assign
            e.src = e.src;
          }
        });

        this.alternativeContent = htmlElementFromContent.body.innerHTML;
      },

      updateDynamicContent(newValue) {
        let htmlElementFromContent = parseToHtml(newValue);

        htmlElementFromContent.querySelectorAll("img").forEach(e => {
          // If the image is not from external source
          if (e.dataset.image !== "") {
            // eslint-disable-next-line no-self-assign
            e.src = e.src;
          }
        });

        this.dynamicContent = htmlElementFromContent.body.innerHTML;
      },
      showHeaderChange(val) {
        if (!val) {
          this.form.header = null;
        }
      },
      showFooterChange(val) {
        if (!val) {
          this.form.footer = null;
        }
      },
      showPopupChange(val) {
        if (!val) {
          this.form.modal = null;
        }
      },
      showSidebarChange(val) {
        if (!val) {
          this.form.sidebar = null;
        }
      },
      showFavoritesBlockChange(val) {
        if (!val) {
          this.form.favoritesBlock = null;
        }
      },
      async previewPageVersion() {
        try {
          await this.preview(this.latestPageSlug, this.page.domain.replace("/api/domains/", ""), this.selectedVersion);
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        }
      },
      handleDisplayVersionHistoryModal() {
        this.displayVersionHistoryModal = true;
      },
      handleVersionChange(version) {
        this.selectedVersion = version;
      },
      async confirmVersionChange() {
        await this.changePageVersion(this.selectedVersion);
        this.selectedVersion = null;
        this.displayVersionHistoryModal = false;
      },
      cancelVersionChange() {
        this.displayVersionHistoryModal = false;
        this.selectedVersion = null;
      },
      async changePageVersion(version) {
        try {
          this.changePageVersionLoading = true;
          const { data } = await this.$admin.dataProvider.customHttpCall(
            "GET",
            `pages/${this.page.id}/version/${version}`,
          );

          for (const [dataKey, dataValue] of Object.entries(data)) {
            this.form[dataKey] = dataValue;
          }

          this.updatePreHeader(convertAssetsPathToAbsoluteLinks(this.form.preHeader));
          this.updateExcerptText(convertAssetsPathToAbsoluteLinks(this.form.excerpt));
          this.updateMainContent(convertAssetsPathToAbsoluteLinks(this.form.mainContent));
          this.versionKey = version;

          this.form.thumbnailImage = data?.thumbnailImage?.["@id"];
          this.form.featuredImage = data?.featuredImage?.["@id"];

          this.alternativeContentVisible = !!data?.sharedAlternativeContent || !!data?.loneAlternativeContent;

          if (data?.loneAlternativeContent) {
            this.updateAlternativeContent(convertAssetsPathToAbsoluteLinks(data.loneAlternativeContent?.content));
            this.alternativeContentConditions = data.loneAlternativeContent?.conditionals;

            this.form.loneAlternativeContent = data.loneAlternativeContent;
            this.form.loneAlternativeContent.headline = data.loneAlternativeContent?.headline;
            this.form.sharedAlternativeContent = this.pageAlternativeContent["@id"];
            this.createPageAlternativeContent = true;
          }

          this.dynamicContentVisible = !!data?.calendar || !!data?.calendarContent;
          this.updateDynamicContent(convertAssetsPathToAbsoluteLinks(data?.calendarContent?.content));

          const newCurrentVersion = this.pageVersions.find(versionItem => versionItem.version === version);
          this.setVersionItems(this.pageVersions, newCurrentVersion);

          this.showRingbaParameters = data?.ringbaParameters?.length || false;

          this.$nextTick(() => {
            this.form.ringbaParameters = data?.ringbaParameters || [];
          });
        } catch (e) {
          this.$admin.toast.error(formErrorMessage(e, this.$t("generic.error")));
        } finally {
          this.changePageVersionLoading = false;
        }
      },

      submit(toastText, linkText) {
        this.successRedirectLink = {
          toastText,
          linkText,
          linkPath: this.fullUrl,
        };
        // Need this timeout for toast text to be displayed
        setTimeout(() => {
          this.setSubmitAttempted();

          if (
            this.imageUploadQueue?.length > 0 ||
            this.excerptImageUploadQueue?.length > 0 ||
            this.alternativeContentImageUploadQueue?.length > 0 ||
            this.dynamicContentImageUploadQueue?.length > 0 ||
            this.preHeaderImageUploadQueue?.length > 0
          ) {
            this.$admin.toast.error(this.$t("resources.pages.images_still_uploading"));
            this.finishedSaving();

            return;
          }

          this.$refs.form.onSubmit();
        }, 0);
      },
      handleRingbaSwitch(val) {
        if (!val) {
          this.form.ringbaParameters = [];
        } else {
          this.form.ringbaParameters = [
            {
              tag: "",
              number: "",
            },
          ];
        }
      },
    },
  };
</script>
<style lang="scss" scoped>
  @import "../../@core/preset/preset/functions.scss";
  @import "../../@core/preset/preset/typography/index.scss";

  .page-subcard-disclaimer {
    margin-top: 8px;
    @include text-caption();
    color: get-color("subtitle");
  }
  .pre-header-input {
    .label {
      color: get-color("subtitle");
      @include text-caption-bold();
    }
    .pre-header-wrapper {
      border: 1px solid get-color("border");
      border-radius: 4px;
      padding: 11px 12px;
      color: get-color("subtitle");
      .pre-header-input {
        transition: 0.3s ease-in-out;
        &.visible {
          margin-top: 6px;
          opacity: 1;
        }
        &.hidden {
          height: 0;
          opacity: 0;
          overflow: hidden;
        }
      }
      .input-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 14px;

        .v-input--switch {
          &::v-deep .v-input--selection-controls__input {
            margin: 0;
          }
        }
      }
    }
  }

  .card-title {
    margin-bottom: 16px;
    .card-icon {
      color: get-color("icon") !important;
      margin-right: 6px;
    }
  }

  .url-wrapper {
    margin-top: 8px;
    @include text-caption();
    color: get-color("subtitle");
    display: flex;
    justify-content: space-between;
    align-items: center;

    a {
      color: get-color("body");
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &:hover {
        color: get-color("primary");
      }
    }
  }

  .upload-history-block {
    border-top: 1px dashed get-color("border");
    padding-top: 16px;
    margin-top: 16px;

    &__first-line {
      @include text-body-2();
      color: get-color("subtitle");
      margin-right: 8px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__status-chip {
      @include text-caption();
      height: 24px;
    }
    &__title {
      padding-right: 4px;
    }
    &__content {
      @include text-body-2();
      color: get-color("subtitle");
    }
    &__status {
      text-transform: capitalize;
    }
    &__label {
      @include text-caption-bold();
      color: get-color("subtitle");
    }
    &__info {
      @include text-body-2();
      color: get-color("subtitle");
      margin-right: 8px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &__view-link {
      @include text-button-small();
      display: flex;
      align-items: center;
      color: get-color("secondary-btn");
      cursor: pointer;
      white-space: nowrap;

      &:hover {
        color: get-color("same-hover");
      }
    }
  }

  .page-version-block {
    &__label {
      @include text-caption-bold();
      color: get-color("subtitle");
      margin-bottom: 6px;
    }
    &__info {
      @include text-body-2();
      color: get-color("subtitle");
      margin-right: 8px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    &__view-history {
      @include text-button-small();
      color: get-color("secondary-btn");
      cursor: pointer;
      white-space: nowrap;

      &:hover {
        color: get-color("same-hover");
      }
    }
  }

  .preview_selected_version {
    @include text-caption();
    color: get-color("subtitle");
    margin-top: 8px;
  }

  .preview-version-label {
    @include text-caption();
    color: get-color("primary");
    cursor: pointer;
    margin-top: 8px;
    i {
      color: get-color("primary");
    }
  }

  .in-use-chip {
    height: 24px;
    &::v-deep {
      .v-chip__content {
        @include text-caption();
      }
    }
  }

  .status-chip {
    @include text-caption();
    height: 24px;

    &::v-deep {
      &.v-chip.success {
        background-color: get-color("success-120") !important;
        .v-chip__content {
          color: get-color("success");
        }
      }
    }
  }

  .chip-outlined {
    background: none !important;
    border: 1px solid get-color("subtitle");
  }

  .details-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .alternative-content {
    &__title {
      @include heading-4();
      color: get-color("body");
      margin-bottom: 4px;
    }
    &__subtitle {
      @include text-caption();
      color: get-color("subtitle");
    }
    &__add-btn {
      min-width: 90px !important;
      max-width: 90px !important;

      &:hover {
        background: get-color("primary-10-hover");
      }

      ::v-deep {
        svg path {
          fill: get-color("secondary-btn") !important;
        }

        span {
          color: get-color("secondary-btn") !important;
        }
      }
    }
    &__delete-btn {
      &:hover {
        background: get-color("primary-10-hover");
      }
      &::v-deep {
        .v-icon {
          margin-right: 6px;
        }
      }
    }
    .enable-utm-parameters-switch {
      margin-right: 2px;
      &::v-deep {
        .v-input--selection-controls__input {
          margin-right: 6px;
        }
      }
    }

    .max-w-50 {
      max-width: 50%;
    }

    .prepend-icon-hidden {
      &::v-deep {
        .v-input__prepend-inner {
          display: none;
        }
        .v-input.v-select.v-select--is-multi.v-autocomplete--with-label .v-label,
        .v-input.v-autocomplete.v-select--is-multi.v-autocomplete--with-label .v-label {
          left: 0 !important;
        }
        .v-input.v-select.v-select--is-multi .v-label {
          left: 0 !important;
        }
      }
    }
  }

  .pass-query-parameters-label {
    @include text-body-2();
    color: get-color("subtitle");
  }
  .pass-query-parameters-switch {
    &::v-deep {
      .v-input--selection-controls__input {
        margin-right: 0;
      }
    }
  }

  .ringba-phone-input-caption {
    @include text-caption();
    color: get-color("subtitle");
  }

  .ringba-block {
    padding-bottom: 16px;
    margin-bottom: 16px;
    border-bottom: 1px dashed get-color("border");
  }

  .label-title {
    @include text-body-2();
  }

  .label-badge {
    // Important needed to overwrite default avatar properties
    border: 1px solid get-color("border") !important;
  }

  .label-wrapper {
    @include text-button-small();
    color: get-color("secondary-btn");
    white-space: nowrap;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;

    i {
      transition: none;
      color: get-color("secondary-btn") !important;
    }

    &:hover {
      cursor: pointer;
      color: get-color("same-hover");

      i {
        color: get-color("same-hover") !important;
      }
    }
  }

  #label-creator {
    border: 1px solid get-color("border");
    border-radius: 4px;
    padding: 16px;
  }

  .label-select-item {
    padding: 8px 0;
    &:hover {
      .label-select-item__close-icon {
        display: inline-flex !important;
      }
    }
    &__close-icon {
      margin-left: auto;
      margin-right: 16px;
    }
  }

  .page-elements-card-subtitle {
    @include text-body-2();
    color: get-color("subtitle");
    margin: 0;
  }

  .label-selector {
    margin-top: 16px;
    margin-bottom: 4px;
  }

  .page-alternative-content-click-parameters-title {
    @include text-body-2-bold();
    color: get-color("subtitle");
    margin-bottom: 12px;
  }

  .page-elements-panel-wrapper {
    ::v-deep .v-expansion-panel-header {
      min-height: unset;
    }
  }

  .page-element-autocomplete {
    ::v-deep {
      .v-input.v-select.v-autocomplete--with-label,
      .v-input.v-autocomplete.v-autocomplete--with-label {
        padding-top: 25px;
      }
    }
  }
  .labels-empty-subtitle {
    @include text-subtitle-2();
    color: get-color("subtitle") !important;
    margin-bottom: 0;
  }

  .label-card-title-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .label-add-button {
    color: get-color("secondary-btn");
    @include text-button-medium();
    display: flex;
    align-items: center;

    &:hover {
      cursor: pointer;
      color: get-color("same-hover");

      ::v-deep path {
        fill: get-color("same-hover") !important;
      }
    }
  }

  .label-cancel-button {
    border-color: get-color("icon");
  }

  .label-create-button-wrapper {
    display: flex;
    gap: 16px;
    margin-top: 16px;

    button {
      flex: 1;
    }
  }

  .label-form {
    ::v-deep {
      .v-messages__message {
        margin-top: 4px !important;
        margin-bottom: 8px;
      }
    }
  }

  .label-name-input {
    margin-bottom: 2px;
  }

  .label-primary-select {
    margin-bottom: 2px;

    ::v-deep {
      .v-input__append-inner {
        padding: 0;

        svg {
          width: 20px;
          height: 20px;
        }
      }
    }
  }

  .additional-label-autocomplete {
    ::v-deep {
      .v-select__selections input::placeholder {
        color: get-color("subtitle") !important;
        opacity: 1 !important;
      }

      .v-list-item {
        @include text-body-2();
      }
    }
  }

  .additional-label-autocomplete:not(.v-autocomplete--search-added) {
    ::v-deep {
      .v-select__selections::after {
        position: absolute;
        content: attr(data-placeholder);
        color: get-color("subtitle") !important;
        opacity: 1 !important;
      }
    }
  }

  .additional-label-item-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .additional-label-item-inner-wrapper {
    display: flex;
    align-items: center;
  }

  .additional-label-chip {
    @include text-caption();
  }

  .dynamic-content {
    &__title {
      @include heading-4();
      color: get-color("body");
      margin-bottom: 4px;
    }
    &__subtitle {
      @include text-caption();
      color: get-color("subtitle");
    }
    &__add-btn {
      min-width: 90px !important;
      max-width: 90px !important;

      &:hover {
        background: get-color("primary-10-hover");
      }

      ::v-deep {
        svg path {
          fill: get-color("secondary-btn") !important;
        }

        span {
          color: get-color("secondary-btn") !important;
        }
      }
    }
    &__delete-btn {
      &:hover {
        background: get-color("primary-10-hover");
      }
      &::v-deep {
        .v-icon {
          margin-right: 6px;
        }
      }
    }
  }

  .error-wrapper {
    margin-top: 8px;
    @include text-caption();
    color: get-color("error");
  }

  .server-side-cloaking-wrapper {
    margin-top: 16px;
    margin-bottom: 16px;
    @include text-body-2();
    color: get-color("body");
    background: get-color("element-bg-200");
    padding: 12px;
    border-radius: 4px;

    & > div {
      display: flex;
      align-items: center;
      justify-content: space-between;

      &::v-deep {
        .v-input--selection-controls__input {
          margin-right: 0;
        }
      }
    }
  }
  .create-alternative-content-item {
    border-top: 1px solid get-color("border");
    margin: 0 8px;
    padding: 0 8px;
    cursor: pointer;
  }

  .create-alternative-content-action {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-direction: row;

    width: 100%;
    justify-content: flex-start;

    & > span {
      @include text-caption();
      color: get-color("primary");
    }
  }

  .create-alternative-content-select {
    margin-top: 16px;
    margin-bottom: 16px;

    ::v-deep {
      .v-input__append-inner {
        padding: 0;

        svg {
          width: 20px;
          height: 20px;
        }
      }
    }
  }

  .alternative-content-item {
    display: flex;
    align-items: center;
    justify-content: space-between;

    & > a {
      display: flex;
      align-items: center;
      gap: 4px;
      @include text-body-2();
      color: get-color("primary");
      text-decoration: none;
    }
  }

  .alternative-no-url-disclaimer {
    margin-top: 16px;
    border-radius: 4px;
    border: 1px solid get-color("accent");
    background: get-color("accent-120");
    padding: 11px 16px;
    display: flex;
    gap: 12px;
    align-items: center;
    @include text-caption();

    &-link {
      color: get-color("primary");
      text-decoration: none;
      cursor: pointer;
    }
  }

  .ab-test-label {
    @include text-caption-bold();
    color: get-color("subtitle");
    display: flex;
    gap: 6px;
    align-items: center;
    margin-bottom: 8px;
  }

  .active-chip {
    color: get-color("info");
  }

  .status-chip {
    @include text-caption();
    height: unset;
    padding: 4px 12px;
  }

  .ab-test-name {
    @include text-caption();
    color: get-color("body");
    text-decoration: underline;
    cursor: pointer;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;

    &:hover {
      color: get-color("primary");
    }
  }

  .inactive-chip {
    background-color: get-color("element-bg-100") !important;
    border: 1px solid get-color("border") !important;
    .v-chip__content {
      color: get-color("disabled") !important;
    }
  }

  .ringba-tag-select-item {
    padding: 8px 0;
    &:hover {
      .ringba-tag-select-item__close-icon {
        display: inline-flex !important;
      }
    }
    &__close-icon {
      margin-left: auto;
      margin-right: 16px;
    }
  }

  .ringba-tag-selector {
    margin-top: 16px;
    margin-bottom: 4px;
  }

  .ringba-tags-empty-subtitle {
    @include text-subtitle-2();
    color: get-color("subtitle") !important;
    margin-bottom: 0;
  }

  .ringba-tag-card-title-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .ringba-tag-add-button {
    color: get-color("secondary-btn");
    @include text-button-medium();
    display: flex;
    align-items: center;

    &:hover {
      cursor: pointer;
      color: get-color("same-hover");

      ::v-deep path {
        fill: get-color("same-hover") !important;
      }
    }
  }

  .ringba-tag-cancel-button {
    border-color: get-color("icon");
  }

  .ringba-tag-create-button-wrapper {
    display: flex;
    gap: 16px;
    margin-top: 16px;

    button {
      flex: 1;
    }
  }

  .ringba-tag-form {
    ::v-deep {
      .v-messages__message {
        margin-top: 4px !important;
        margin-bottom: 8px;
      }
    }
  }

  .ringba-tag-name-input {
    margin-bottom: 2px;
  }

  .ringba-tag-primary-select {
    margin-bottom: 2px;

    ::v-deep {
      .v-input__append-inner {
        padding: 0;

        svg {
          width: 20px;
          height: 20px;
        }
      }
    }
  }
  .additional-label-item-inner-wrapper {
    display: flex;
    align-items: center;
  }

  .ringba-tag-item-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
</style>
